import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEquals } from '@fortawesome/pro-solid-svg-icons/faEquals';
import React, { useState, useCallback } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Popover } from 'ui-components/components/popover';
import { noop } from 'ui-components/default-props/noop';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { messages } from './messages';
import style from './style.module.less';
import { addInventoryForProduct, removeInventoryForProduct } from '../../../../../../../store/inventory/inventory.api';
import { PopoverNotesInput } from '../../shared-components/popover-notes';
import { QuantityInput } from '../../shared-components/quantity-input';
import sharedStyle from '../style.module.less';

/**
 * Component for displaying inventory set value popover
 * @param {object} props
 * @param {string} props.inventoryLocationId
 * @param {() => void} props.onReloadLocations
 * @param {string} props.productId
 * @param {number} props.stock
 * @param {boolean} props.isPopoverOpen
 * @param {() => void} props.onClose
 * @param {string} [props.lotUuid]
 * @returns {JSX.Element}
 */
export const SetInventoryPopover = ({
  inventoryLocationId,
  onReloadLocations,
  productId,
  stock,
  isPopoverOpen,
  onClose = noop,
  lotUuid = '',
}) => {
  const [isOpen, setIsOpen] = useAsyncSafeState(isPopoverOpen);
  const [quantity, setQuantity] = useState(0);
  const [notes, setNotes] = useState('');
  const clearInputs = useCallback(() => {
    setQuantity(0);
    setNotes('');
  }, [setQuantity, setNotes]);
  const onOpenPopover = useCallback(() => {
    clearInputs();
    setIsOpen(!isOpen);
  }, [clearInputs, setIsOpen, isOpen]);
  const onOuterAction = useCallback(() => {
    setIsOpen(false);
    onClose();
  }, [setIsOpen, onClose]);

  const shouldAdd = stock < quantity;
  const quantityForAdjustment = shouldAdd ? quantity - stock : stock - quantity;
  const adjustProps = {
    productId,
    inventoryLocationId,
    quantity: quantityForAdjustment,
    description: notes,
    lotUuid,
  };

  const onAdjustInventory = useCallback(() => {
    const adjustInventoryPromise = shouldAdd ? addInventoryForProduct(adjustProps) : removeInventoryForProduct(adjustProps);
    adjustInventoryPromise.promise.then(() => {
      setIsOpen(false);
      if (shouldAdd) {
        onReloadLocations();
      }
    });
    return () => adjustInventoryPromise?.cancel();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- adjustProps changes every render
  }, [shouldAdd, adjustProps, setIsOpen, onReloadLocations]);
  const isValidNumber = !Number.isNaN(quantity);

  const setInventoryPopopver = (
    <div data-ignore-root-focus>
      <div className={sharedStyle['popover-title']}>
        <FormattedMessage {...messages.setInventoryPopOverTitle} />
      </div>
      <div className={classnames(sharedStyle['popover-body'], style['adjust-inventory'])}>
        <div className={style['body-inputs']}>
          <QuantityInput onChange={setQuantity} quantity={quantity} />
          <PopoverNotesInput notes={notes} setNotes={setNotes} />
        </div>
      </div>
      <div className={sharedStyle['popover-buttons']}>
        <Button onClick={onOuterAction} theme='tertiary'>
          <FormattedMessage {...messages.setInventoryPopoverCancel} />
        </Button>
        <Button disabled={!isValidNumber} onClick={onAdjustInventory} theme='primary'>
          <FormattedMessage {...messages.setInventoryRemoveButton} />
        </Button>
      </div>
    </div>
  );

  const popoverProps = {
    isOpen,
    preferPlace: 'below',
    enterExitTransitionDurationMs: 0,
    body: setInventoryPopopver,
    appendTarget: document.querySelector('#app-root'),
    onOuterAction,
  };

  return (
    <Popover {...popoverProps}>
      <Button className={sharedStyle['adjust-icon']} onClick={onOpenPopover} theme='link'>
        {!isPopoverOpen && <FontAwesomeIcon className={sharedStyle.icon} icon={faEquals} />}
      </Button>
    </Popover>
  );
};
